import { Dropdown, InputNumber, Tooltip } from 'antd';
import debounce from 'lodash/debounce';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { SprintPoints } from 'api/sprint-planning/types';
import { TaskAPI } from 'api/tasks';
import { Menu } from 'components/atoms/dropdown';
import { Loading } from 'components/atoms/loading';
import { MAX_POINTS_DISPLAY } from 'components/organisms/tasks/constants';
import { Label01 } from 'components/styles';
import { useTaskField } from 'data/fields/hooks';
import { useTranslation } from 'hooks/use-translation';
import { useGetSprintConfiguration } from 'queries/sprint-planning';

interface PointProps {
  points: number | undefined;
  project: number;
  taskId: number;
}

const CircleContainer = styled.div`
  ${Label01};
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 20px;
  border-radius: var(--border-radius-curved);
  background-color: var(--gray-10);
  color: var(--black-100);
  &:hover {
    background: var(--hover-darker-color);
  }
`;

const EditableContainer = styled.div`
  padding-right: var(--spacing-03);
`;

const StyledInputNumber = styled(InputNumber)`
  width: 56px;
  padding-left: var(--spacing-03);
  height: 24px;
  align-items: center;
  display: flex;
  justify-content: center;
`;
const StyledMenu = styled(Menu)`
  width: 160px;
  padding: var(--spacing-03) 0;
`;
const MenuItem = styled(Menu.Item)<{ isSelected: boolean }>`
  display: flex;
  align-items: baseline;
  width: 100%;
  gap: var(--spacing-03);
  padding: var(--spacing-03) var(--spacing-05);
  cursor: pointer;
  background: ${({ isSelected }) => (isSelected ? 'var(--blue-10)' : 'none')};
  color: ${({ isSelected }) => (isSelected ? 'var(--blue-60)' : 'inherit')};

  &:hover {
    background: var(--hover-darker-color);
  }
`;
const PointsLoading = styled(Loading).attrs({ size: 'small' })``;
const renderPointsDisplay = (
  isUpdating: boolean,
  points: number | undefined,
  getLabel: (value: number) => string | undefined,
): React.ReactNode => {
  if (isUpdating) {
    return <PointsLoading />;
  }

  if (points && points > 0) {
    if (points > MAX_POINTS_DISPLAY) {
      return `${MAX_POINTS_DISPLAY}+`;
    }

    return getLabel(points) || points;
  }

  return '-';
};

export const PointsSprint: React.FC<PointProps> = ({
  points,
  project,
  taskId,
}) => {
  const { data: sprintConfig } = useGetSprintConfiguration();
  const getPoints = (pointType): string => {
    switch (pointType) {
      case SprintPoints.Linear:
        return 'pointsLinear';
      case SprintPoints.Fibonacci:
        return 'pointsFibonacci';
      case SprintPoints.Exponential:
        return 'pointsExponential';
      case SprintPoints.Custom:
        return 'pointsCustom';
      default:
        throw new Error('invalid');
    }
  };
  const pointType = getPoints(sprintConfig?.pointType);

  const taskField = useTaskField(`${pointType}`);

  const [currentValue, setCurrentValue] = useState<number | undefined>(points);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const pointsFieldId = useMemo(() => {
    const pointsField = taskField?.fieldId;

    return pointsField;
  }, [taskField]);

  const getLabel = (value: number): string | undefined => {
    return taskField?.metaData?.options?.find(
      (option) => option.value === value,
    )?.label;
  };

  const debouncedUpdateTask = useMemo(
    () =>
      debounce((value) => {
        setIsUpdating(true);
        TaskAPI.updateTask(project, taskId, {
          fields: [{ fieldId: pointsFieldId, fieldValue: value }],
        }).finally(() => {
          setTimeout(() => {
            setIsUpdating(false);
          }, 1500);
        });
      }, 500),
    [project, taskId, pointsFieldId, setIsUpdating],
  );

  const handleMenuClick = useCallback(
    (info: { key: React.Key }) => {
      const selectedValue = Number(info.key);

      debouncedUpdateTask(selectedValue);
      setIsEditing(false);
    },
    [debouncedUpdateTask],
  );

  const handleNumberChange = useCallback(
    (value: string | number | null | undefined) => {
      if (typeof value === 'number' && value !== null) {
        setCurrentValue(value);
        debouncedUpdateTask(value);
      }
    },
    [debouncedUpdateTask],
  );

  const handleMouseEnter = () => {
    setIsEditing(true);
  };

  const handleMouseLeave = () => {
    setIsEditing(false);
  };

  const toggleEditing = (event: React.MouseEvent<HTMLDivElement>) => {
    setIsEditing(!isEditing);
    event.stopPropagation();
  };
  const pointsHeader = useTranslation('sprint.member.points');

  const menu = (
    <StyledMenu onClick={(info) => handleMenuClick(info)}>
      <Menu.Header>{pointsHeader}</Menu.Header>

      {taskField?.metaData?.options?.map((option) => (
        <MenuItem
          key={option.value}
          isSelected={option.value === points}
          onClick={(e) => e.domEvent.stopPropagation()}
        >
          {option.label}
        </MenuItem>
      ))}
    </StyledMenu>
  );

  return sprintConfig?.pointType !== SprintPoints.Custom ? (
    <Tooltip title={pointsHeader}>
      <Dropdown overlay={menu} trigger={['click']} className="always-visible">
        <CircleContainer onClick={(e) => e.stopPropagation()}>
          {renderPointsDisplay(isUpdating, points, getLabel)}
        </CircleContainer>
      </Dropdown>
    </Tooltip>
  ) : (
    <div>
      {isEditing ? (
        <EditableContainer onClick={(e) => e.stopPropagation()}>
          <StyledInputNumber
            min={0}
            value={currentValue}
            onChange={handleNumberChange}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        </EditableContainer>
      ) : (
        <Tooltip title={pointsHeader}>
          <CircleContainer onClick={toggleEditing}>
            {renderPointsDisplay(isUpdating, points, getLabel)}
          </CircleContainer>
        </Tooltip>
      )}
    </div>
  );
};
